import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, MapPin, Store, Globe, Phone, Mail, Minus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Retailers() {
  const [retailers, setRetailers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const retailersSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Forside",
        "item": `${window.location.origin}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Forhandlere",
        "item": `${window.location.origin}/forhandlere`
      }
    ]
  };

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/retailers')
      .then(res => res.json())
      .then(data => {
        setRetailers(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching retailers:', err);
        setLoading(false);
      });
  }, []);

  const filteredRetailers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return retailers;

    const isNumeric = /^\d+$/.test(query);
    const searchNum = isNumeric ? parseInt(query, 10) : null;

    const matches = retailers.map(retailer => {
      const zipNum = parseInt(retailer.zip_code, 10);
      const nameLower = retailer.name.toLowerCase();
      const cityLower = retailer.city.toLowerCase();
      const zipStr = retailer.zip_code.toLowerCase();

      let score = null; // null betyder intet match

      if (isNumeric && !isNaN(zipNum)) {
        // Numerisk søgning (f.eks. postnummer)
        if (zipStr === query) {
          score = 0; // Præcist postnummer match
        } else if (zipStr.startsWith(query)) {
          score = 1; // Postnummer starter med søgningen (f.eks. "78" matcher "7800" med høj prioritet)
        } else if (query.length >= 3) {
          // Nærhedssøgning hvis søgningen er på mindst 3 cifre
          const diff = Math.abs(searchNum - zipNum);
          if (diff <= 100) {
            score = diff + 10; // Nærhedsmatch (lavere differens = bedre score)
          }
        }
        
        // Tjek også om butiksnavn eller by indeholder tallet (f.eks. "Bazar No2" indeholder "2")
        if (score === null && (nameLower.includes(query) || cityLower.includes(query))) {
          score = 200;
        }
      } else {
        // Almindelig tekstsøgning (f.eks. bynavn eller butiksnavn)
        if (cityLower === query) {
          score = 0; // Præcist bynavn match
        } else if (nameLower === query) {
          score = 1; // Præcist butiksnavn match
        } else if (cityLower.startsWith(query)) {
          score = 5; // Bynavn starter med søgningen
        } else if (nameLower.startsWith(query)) {
          score = 10; // Butiksnavn starter med søgningen
        } else if (cityLower.includes(query)) {
          score = 20; // Bynavn indeholder søgningen
        } else if (nameLower.includes(query)) {
          score = 30; // Butiksnavn indeholder søgningen
        } else if (zipStr.includes(query)) {
          score = 40; // Postnummer indeholder søgningen (f.eks. "00")
        }
      }

      return { retailer, score };
    });

    // Filtrer ikke-matchende fra og sorter efter relevans/nærhed (laveste score først)
    return matches
      .filter(m => m.score !== null)
      .sort((a, b) => a.score - b.score)
      .map(m => m.retailer);
  }, [retailers, searchQuery]);

  return (
    <div className="bg-white min-h-screen pb-32">
      <SEO 
        title="Find forhandlere - Se butikker her"
        description="Find din nærmeste Cards by Eve forhandler. Vores håndtegnede kort og illustrationer forhandles i hyggelige blomster- og gavebutikker over hele landet."
        schema={retailersSchema}
      />
      {/* Header */}
      <header className="pt-24 pb-20 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-6xl md:text-8xl font-serif text-brand-950 leading-tight mb-8">
            Mine <br />
            <span className="italic">forhandlere</span>
          </h1>
          <p className="text-brand-600 text-lg font-light max-w-2xl leading-relaxed">
            Find Cards by Eve i nøje udvalgte butikker over hele landet. Jeg samarbejder med partnere, der deler min passion for æstetik og kvalitet.
          </p>
        </div>
      </header>

      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-24">
            
            {/* Left: Search & Info */}
            <div className="lg:col-span-4 space-y-12">
              <div className="space-y-6">
                <h2 className="text-3xl font-serif text-brand-950 italic">Find din nærmeste butik</h2>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-0 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-brand-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Søg på by eller postnummer..."
                    className="block w-full pl-8 pr-4 py-4 border-b border-brand-200 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 bg-transparent"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="p-10 bg-brand-50 rounded-sm border border-brand-100">
                <MapPin className="w-8 h-8 text-brand-950 mb-6" />
                <h3 className="text-xl font-serif text-brand-950 mb-4">Hele landet</h3>
                <p className="text-brand-600 font-light text-sm leading-relaxed">
                  Findes i smukke og hyggelige blomster- og gavebutikker.
                </p>
              </div>
            </div>

            {/* Right: Retailer Grid */}
            <div className="lg:col-span-8">
              {loading ? (
                <div className="flex justify-center py-32">
                  <div className="w-10 h-10 border-t-2 border-brand-950 rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
                  {filteredRetailers.length > 0 ? (
                    filteredRetailers.map((retailer, index) => (
                      <motion.div 
                        key={retailer.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.8 }}
                        className=""
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="space-y-1">
                            <h3 className="text-2xl font-serif text-brand-950 transition-colors">{retailer.name}</h3>
                            <p className="text-brand-500 font-light text-sm">{retailer.address}</p>
                            <p className="text-brand-500 font-light text-sm">{retailer.zip_code} {retailer.city}</p>
                          </div>
                          {retailer.website && (
                            <a 
                              href={retailer.website.startsWith('http') ? retailer.website : `https://${retailer.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-brand-950 hover:text-brand-500 transition-colors mt-1"
                            >
                              <Globe className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <div className="h-px w-full bg-brand-100 transition-colors duration-500"></div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-span-full py-20 text-center border border-dashed border-brand-200">
                      <p className="text-brand-400 font-light italic">Ingen forhandlere fundet på din søgning.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Become a Retailer CTA */}
              <div className="mt-24 p-12 bg-brand-950 text-white relative overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
                  <div className="max-w-md">
                    <h3 className="text-3xl font-serif mb-4 italic">Vil du være med på rejsen?</h3>
                    <p className="text-brand-200 font-light text-sm leading-relaxed">
                      Jeg søger altid nye passionerede samarbejdspartnere i nye postnumre. Kontakt mig for at høre mere om mulighederne for at blive forhandler.
                    </p>
                  </div>
                  <Link 
                    to="/kontakt" 
                    className="bg-white text-brand-950 px-10 py-5 text-xs uppercase tracking-widest font-bold hover:bg-brand-200 transition-all shrink-0 flex items-center group"
                  >
                    Kontakt mig <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
